import { FC, useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  IconButton,
  Drawer,
  Typography,
  Divider,
  InputAdornment,
} from '@mui/material';
import {
  PlusCircleIcon,
  XIcon,
  BracketsAngleIcon,
  QuotesIcon,
  TrashIcon,
  PencilSimpleIcon,
} from '@phosphor-icons/react';
import { useDashboard } from '../../hooks/useDashboard';
import { commonStyles } from '../../utils/styles/inputs';

export interface VariablePair {
  key: string;
  value: string;
}

interface VariablesModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (variables: VariablePair[]) => void;
  initialVariables?: VariablePair[];
  isLoading?: boolean;
}

const VariablesModal: FC<VariablesModalProps> = ({
  open,
  onClose,
  onSave,
  isLoading,
  initialVariables = [],
}) => {
  const { selectedAssistant } = useDashboard();
  const [variables, setVariables] = useState<VariablePair[]>(initialVariables);
  const [isEditMode, setIsEditMode] = useState(false);
  const [hasSaved, setHasSaved] = useState(false); // Track if we just saved

  // Initialize variables from selectedAssistant.config.variables if available
  useEffect(() => {
    if (
      open &&
      selectedAssistant?.config?.variables &&
      selectedAssistant.config.variables.length > 0
    ) {
      setVariables(selectedAssistant.config.variables);
    } else if (open) {
      setVariables([]);
    }
    // Reset to view mode when modal opens
    setIsEditMode(false);
    setHasSaved(false); // Reset save state when modal opens
  }, [open, selectedAssistant]);

  // Auto-close modal after successful save
  useEffect(() => {
    if (isLoading === false && hasSaved) {
      // Close modal after a brief delay to show completion
      const timer = setTimeout(() => {
        onClose();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isLoading, hasSaved, onClose]);

  const handleChange = (
    index: number,
    field: 'key' | 'value',
    value: string
  ) => {
    const updatedVariables = [...variables];
    updatedVariables[index][field] = value;
    setVariables(updatedVariables);
  };

  const handleAddVariable = () => {
    setVariables([...variables, { key: '', value: '' }]);
  };

  const handleRemoveVariable = (index: number) => {
    if (variables.length > 1) {
      const updatedVariables = variables.filter((_, i) => i !== index);
      setVariables(updatedVariables);
    }
  };

  const handleSave = () => {
    const filteredVariables = variables.filter(
      variable => variable.key.trim() !== '' || variable.value.trim() !== ''
    );
    setHasSaved(true);
    onSave(filteredVariables);
  };

  // Switch to edit mode (from "Add New Variable" button)
  const handleAddVariables = () => {
    if (variables.length === 0) {
      setVariables([{ key: '', value: '' }]);
    }
    setIsEditMode(true);
  };

  // Switch to edit mode (from pencil icon on variable cards)
  const handleEditVariable = () => {
    setIsEditMode(true);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: '637px',
            p: 0,
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            display: 'flex',
            flexDirection: 'column',
          },
        },
      }}
    >
      {/* Sticky Header Section */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          backgroundColor: '#FFFFFF',
          px: 2,
          pt: 2,
        }}
      >
        {/* Header Section */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          {/* Close Button and Title */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                '&:hover': { backgroundColor: '#E8EBF1' },
                transition: 'all 0.2s ease',
              }}
            >
              <XIcon size={18} />
            </IconButton>
            <Box>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: 18,
                  color: 'custom.textBlue',
                  letterSpacing: '-0.2px',
                }}
              >
                {isEditMode ? 'Set Variables' : 'Current Variables'}
              </Typography>
            </Box>
          </Box>

          {/* Action Buttons - Show save/cancel buttons only in edit mode */}
          {isEditMode && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={
                  variables.some(
                    variable =>
                      variable.key.trim() === '' || variable.value.trim() === ''
                  ) || isLoading
                }
                sx={{
                  fontSize: 14,
                  fontWeight: 500,
                  borderRadius: '8px',
                  textTransform: 'none',
                  boxShadow: 'none',
                  px: 2,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                  },
                }}
              >
                {isLoading ? 'Saving...' : 'Save'}
              </Button>
            </Box>
          )}
        </Box>

        <Divider
          sx={{
            borderColor: 'rgba(0, 0, 0, 0.08)',
            mb: 2,
            mx: -2,
          }}
        />
      </Box>

      {/* Scrollable Content Area */}
      <Box
        sx={{
          overflow: 'auto',
          flexGrow: 1,
          px: 2,
          pb: 2,
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
      >
        {!isEditMode ? (
          // View Mode - Show variable cards
          <>
            {/* Variables Cards */}
            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}
            >
              {variables.length > 0 ? (
                variables.map((variable, index) => (
                  <Box
                    key={index}
                    sx={{
                      backgroundColor: '#FFFFFF',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      padding: '16px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                    }}
                  >
                    {/* Header row with variable key and edit button */}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: 14,
                          color: '#1F2937',
                          letterSpacing: '-0.1px',
                        }}
                      >
                        {variable.key}
                      </Typography>
                      <IconButton
                        onClick={handleEditVariable}
                        sx={{
                          width: 28,
                          height: 28,
                          backgroundColor: '#F8F9FA',
                          border: '1px solid #E5E7EB',
                          borderRadius: '6px',
                          color: '#6B7280',
                          '&:hover': {
                            backgroundColor: '#E8EBF1',
                            color: '#374151',
                          },
                          transition: 'all 0.2s ease',
                        }}
                      >
                        <PencilSimpleIcon size={18} />
                      </IconButton>
                    </Box>

                    {/* Variable value */}
                    <Typography
                      sx={{
                        fontWeight: 400,
                        fontSize: 14,
                        color: '#6B7280',
                        letterSpacing: '-0.1px',
                        fontStyle: 'italic',
                      }}
                    >
                      {variable.value}
                    </Typography>
                  </Box>
                ))
              ) : (
                <Box
                  sx={{
                    backgroundColor: '#F8F9FA',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    padding: '24px',
                    textAlign: 'center',
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 400,
                      fontSize: 14,
                      color: '#9CA3AF',
                      letterSpacing: '-0.1px',
                    }}
                  >
                    No variables set yet
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Add Variables Button */}
            <Button
              variant="contained"
              color="primary"
              onClick={handleAddVariables}
              startIcon={<PlusCircleIcon size={18} />}
              sx={{
                width: 'fit-content',
                fontSize: 14,
                margin: '0 auto',
                fontWeight: 500,
                borderRadius: '8px',
                textTransform: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                boxShadow: 'none',
                py: 1,
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                },
              }}
            >
              Add New Variable
            </Button>
          </>
        ) : (
          // Edit Mode - Show form fields
          <>
            {/* Form Headers */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 2,
                mb: 2,
              }}
            >
              <Typography sx={commonStyles.formLabel}>Variable Name</Typography>
              <Typography sx={commonStyles.formLabel}>
                Variable Value
              </Typography>
            </Box>

            {/* Variable Form Fields */}
            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}
            >
              {variables.map((variable, index) => (
                <Box
                  key={index}
                  sx={{
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    padding: '16px',
                    display: 'flex',
                    gap: 2,
                    alignItems: 'center',
                  }}
                >
                  <TextField
                    fullWidth
                    placeholder="restaurant_name"
                    value={variable.key}
                    onChange={e => handleChange(index, 'key', e.target.value)}
                    size="small"
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <BracketsAngleIcon size={16} color="#9CA3AF" />
                          </InputAdornment>
                        ),
                      },
                    }}
                    sx={commonStyles.textField}
                  />
                  <TextField
                    fullWidth
                    placeholder="California Burrito"
                    value={variable.value}
                    onChange={e => handleChange(index, 'value', e.target.value)}
                    size="small"
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <QuotesIcon size={16} color="#9CA3AF" />
                          </InputAdornment>
                        ),
                      },
                    }}
                    sx={commonStyles.textField}
                  />
                  <IconButton
                    onClick={() => handleRemoveVariable(index)}
                    disabled={variables.length === 1}
                    sx={{
                      width: 36,
                      height: 36,
                      backgroundColor:
                        variables.length === 1 ? '#F9FAFB' : '#FEF2F2',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      color: variables.length === 1 ? '#D1D5DB' : '#EF4444',
                      '&:hover': {
                        backgroundColor:
                          variables.length === 1 ? '#F9FAFB' : '#FEE2E2',
                        color: variables.length === 1 ? '#D1D5DB' : '#DC2626',
                      },
                      '&:disabled': {
                        backgroundColor: '#F9FAFB',
                        color: '#D1D5DB',
                      },
                    }}
                  >
                    <TrashIcon size={16} />
                  </IconButton>
                </Box>
              ))}
            </Box>

            {/* Add New Variable Button */}
            <Button
              variant="contained"
              color="primary"
              onClick={handleAddVariable}
              startIcon={<PlusCircleIcon size={18} />}
              sx={{
                width: 'fit-content',
                fontSize: 14,
                margin: '0 auto',
                fontWeight: 500,
                borderRadius: '8px',
                textTransform: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                boxShadow: 'none',
                py: 1,
                mb: 4,
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                },
              }}
            >
              Add New Variable
            </Button>
          </>
        )}
      </Box>
    </Drawer>
  );
};

export default VariablesModal;
